import React, { useEffect, useMemo } from "react"

const TOP_TEETH = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28]
const BOTTOM_TEETH = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38]

const TOOTH_LEVEL_CONDITIONS = [
  { value: "SANO", label: "Sano" },
  { value: "CORONA", label: "Corona" },
  { value: "ENDODONCIA", label: "Endodoncia" },
  { value: "IMPLANTE", label: "Implante" },
  { value: "AUSENTE", label: "Ausente" },
  { value: "EXTRAIDO", label: "Extraído" },
  { value: "FRACTURA", label: "Fractura" },
  { value: "MOVILIDAD", label: "Movilidad" },
  { value: "DESGASTE", label: "Desgaste" },
  { value: "PROTESIS", label: "Prótesis" },
  { value: "RESTAURACION", label: "Restauración" },
  { value: "OBSERVACION", label: "Observación" },
]

const SURFACE_LEVEL_CONDITIONS = [
  { value: "CARIES", label: "Caries" },
  { value: "RESTAURACION", label: "Restauración" },
  { value: "RESTAURACION_DEFECTUOSA", label: "Restauración defectuosa" },
  { value: "LESION", label: "Lesión" },
  { value: "SELLADOR", label: "Sellador" },
  { value: "DESGASTE", label: "Desgaste" },
]

const SURFACE_OPTIONS = [
  { value: "M", label: "Mesial" },
  { value: "O", label: "Oclusal" },
  { value: "D", label: "Distal" },
  { value: "V", label: "Vestibular" },
  { value: "L", label: "Lingual / Palatino" },
]

const CONDITION_ALIASES = {
  CARIES: "CARIES",

  RESTORATION: "RESTAURACION",
  RESTAURACION: "RESTAURACION",

  DEFECTIVE_RESTORATION: "RESTAURACION_DEFECTUOSA",
  RESTAURACION_DEFECTUOSA: "RESTAURACION_DEFECTUOSA",

  ENDODONTIC: "ENDODONCIA",
  ENDODONCIA: "ENDODONCIA",

  FRACTURE: "FRACTURA",
  FRACTURA: "FRACTURA",

  EXTRACTED: "EXTRAIDO",
  EXTRAIDO: "EXTRAIDO",

  IMPLANT: "IMPLANTE",
  IMPLANTE: "IMPLANTE",

  HEALTHY: "SANO",
  SANO: "SANO",

  CROWN: "CORONA",
  CORONA: "CORONA",

  MISSING: "AUSENTE",
  AUSENTE: "AUSENTE",

  MOBILITY: "MOVILIDAD",
  MOVILIDAD: "MOVILIDAD",

  WEAR: "DESGASTE",
  DESGASTE: "DESGASTE",

  PROSTHESIS: "PROTESIS",
  PROTESIS: "PROTESIS",

  OBSERVATION: "OBSERVACION",
  OBSERVACION: "OBSERVACION",

  LESION: "LESION",

  SELLADOR: "SELLADOR",
  SEALANT: "SELLADOR",
}

const SURFACE_ALIASES = {
  ALL: "ALL",
  TOOTH: "ALL",
  GENERAL: "ALL",

  O: "O",
  OCLUSAL: "O",
  OCCLUSAL: "O",

  M: "M",
  MESIAL: "M",

  D: "D",
  DISTAL: "D",

  V: "V",
  VESTIBULAR: "V",
  BUCAL: "V",
  LABIAL: "V",

  L: "L",
  LINGUAL: "L",
  PALATINO: "L",
  PALATAL: "L",
}

function normalizeCondition(value) {
  const key = String(value || "").trim().toUpperCase()
  return CONDITION_ALIASES[key] || key
}

function normalizeSurface(value) {
  const key = String(value || "").trim().toUpperCase()
  if (!key) return "ALL"
  return SURFACE_ALIASES[key] || key
}

function getSurfaceLabel(surface) {
  const normalized = normalizeSurface(surface)
  if (normalized === "ALL") return "Diente completo"
  return SURFACE_OPTIONS.find((item) => item.value === normalized)?.label || normalized
}

function getDefinitionType(entry) {
  const raw = String(entry?.definitionType || "").trim().toUpperCase()
  if (raw === "TOOTH" || raw === "GENERAL") return "TOOTH"
  if (raw === "SURFACE") return "SURFACE"

  const surface = normalizeSurface(entry?.surface)
  return surface === "ALL" ? "TOOTH" : "SURFACE"
}

function getConditionMeta(condition) {
  const normalized = normalizeCondition(condition)

  const map = {
    CARIES: {
      label: "Caries",
      bg: "#fef2f2",
      border: "#fecaca",
      color: "#991b1b",
      short: "Ca",
    },
    RESTAURACION: {
      label: "Restauración",
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
      short: "Re",
    },
    RESTAURACION_DEFECTUOSA: {
      label: "Restauración defectuosa",
      bg: "#fff7ed",
      border: "#fed7aa",
      color: "#c2410c",
      short: "RD",
    },
    ENDODONCIA: {
      label: "Endodoncia",
      bg: "#f5f3ff",
      border: "#ddd6fe",
      color: "#6d28d9",
      short: "En",
    },
    CORONA: {
      label: "Corona",
      bg: "#fffbeb",
      border: "#fde68a",
      color: "#a16207",
      short: "Co",
    },
    IMPLANTE: {
      label: "Implante",
      bg: "#f3e8ff",
      border: "#e9d5ff",
      color: "#7e22ce",
      short: "Im",
    },
    AUSENTE: {
      label: "Ausente",
      bg: "#f8fafc",
      border: "#cbd5e1",
      color: "#475569",
      short: "Au",
    },
    EXTRAIDO: {
      label: "Extraído",
      bg: "#f1f5f9",
      border: "#cbd5e1",
      color: "#334155",
      short: "Ex",
    },
    FRACTURA: {
      label: "Fractura",
      bg: "#fff7ed",
      border: "#fdba74",
      color: "#c2410c",
      short: "Fr",
    },
    MOVILIDAD: {
      label: "Movilidad",
      bg: "#fef3c7",
      border: "#fcd34d",
      color: "#92400e",
      short: "Mo",
    },
    DESGASTE: {
      label: "Desgaste",
      bg: "#ecfccb",
      border: "#bef264",
      color: "#3f6212",
      short: "De",
    },
    PROTESIS: {
      label: "Prótesis",
      bg: "#eef2ff",
      border: "#c7d2fe",
      color: "#4338ca",
      short: "Pr",
    },
    OBSERVACION: {
      label: "Observación",
      bg: "#f8fafc",
      border: "#cbd5e1",
      color: "#475569",
      short: "Ob",
    },
    LESION: {
      label: "Lesión",
      bg: "#fee2e2",
      border: "#fca5a5",
      color: "#b91c1c",
      short: "Le",
    },
    SELLADOR: {
      label: "Sellador",
      bg: "#ecfeff",
      border: "#a5f3fc",
      color: "#0f766e",
      short: "Se",
    },
    SANO: {
      label: "Sano",
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
      short: "Sa",
    },
  }

  return (
    map[normalized] || {
      label: normalized,
      bg: "#f8fafc",
      border: "#cbd5e1",
      color: "#334155",
      short: normalized.slice(0, 2),
    }
  )
}

function getStatusLabel(status) {
  const raw = String(status || "").trim().toUpperCase()
  if (raw === "ACTIVE") return "Activo"
  if (raw === "INACTIVE") return "Inactivo"
  if (raw === "RESOLVED") return "Resuelto"
  if (raw === "COMPLETED" || raw === "DONE") return "Realizado"
  return raw || "Activo"
}

function formatDateSafe(value, formatDateTime) {
  if (!value) return ""
  try {
    if (typeof formatDateTime === "function") return formatDateTime(value)
    return new Date(value).toLocaleString("es-MX")
  } catch {
    return ""
  }
}

function serializeEntry(raw) {
  const condition = normalizeCondition(raw?.condition)
  const surface = normalizeSurface(raw?.surface)
  const definitionType = getDefinitionType(raw)

  return {
    ...raw,
    condition,
    conditionLabel: getConditionMeta(condition).label,
    surface: surface === "ALL" ? null : surface,
    surfaceLabel: getSurfaceLabel(surface),
    definitionType,
  }
}

function buildToothState(entries) {
  const state = {}

  for (const tooth of [...TOP_TEETH, ...BOTTOM_TEETH]) {
    state[tooth] = {
      tooth,
      general: [],
      surfaces: {
        O: [],
        M: [],
        D: [],
        V: [],
        L: [],
      },
    }
  }

  for (const raw of entries || []) {
    const entry = serializeEntry(raw)
    const tooth = Number(entry?.tooth)
    if (!state[tooth]) continue

    if (entry.definitionType === "TOOTH") {
      state[tooth].general.push(entry)
    } else if (entry.surface && state[tooth].surfaces[entry.surface]) {
      state[tooth].surfaces[entry.surface].push(entry)
    }
  }

  return state
}

function getDominantGeneralCondition(generalEntries = []) {
  if (!generalEntries.length) return null
  return generalEntries[generalEntries.length - 1]?.condition || null
}

export default function VisualOdontogram({
  entries = [],
  selectedTooth,
  onSelectTooth,
  selectedSurface = "ALL",
  onSelectSurface,
  selectedCondition = "",
  onSelectCondition,
  onSave,
  onSendToPlan,
  saving = false,
  formatDateTime,
}) {
  const toothState = useMemo(() => buildToothState(entries), [entries])

  const normalizedSelectedSurface = normalizeSurface(selectedSurface)
  const selectedMode = normalizedSelectedSurface === "ALL" ? "TOOTH" : "SURFACE"
  const conditionOptions =
    selectedMode === "TOOTH" ? TOOTH_LEVEL_CONDITIONS : SURFACE_LEVEL_CONDITIONS

  const currentToothData = selectedTooth ? toothState[selectedTooth] : null
  const odontogramSummary = useMemo(() => {
    const allTeeth = [...TOP_TEETH, ...BOTTOM_TEETH]

    let teethWithFindings = 0
    let generalConditions = 0
    let surfacesWithFindings = 0
    let totalEntries = 0

    allTeeth.forEach((tooth) => {
      const current = toothState[tooth]
      if (!current) return

      const general = current.general || []
      const surfaces = current.surfaces || {}

      const surfaceGroups = Object.values(surfaces).filter(
        (items) => Array.isArray(items) && items.length > 0
      )

      const toothTotal =
        general.length + Object.values(surfaces).flat().length

      if (toothTotal > 0) {
        teethWithFindings += 1
      }

      generalConditions += general.length
      surfacesWithFindings += surfaceGroups.length
      totalEntries += toothTotal
    })

    return {
      teethWithFindings,
      generalConditions,
      surfacesWithFindings,
      totalEntries
    }
  }, [toothState])

  useEffect(() => {
    if (!selectedTooth) return

    const normalized = normalizeCondition(selectedCondition)
    const isValid = conditionOptions.some((item) => item.value === normalized)

    if (!isValid) {
      onSelectCondition?.(selectedMode === "TOOTH" ? "OBSERVACION" : "CARIES")
    }
  }, [
    selectedTooth,
    selectedCondition,
    selectedMode,
    conditionOptions,
    onSelectCondition,
  ])

  const closeEditor = () => {
    onSelectTooth?.(null)
    onSelectSurface?.("ALL")
  }

  const switchToToothMode = () => {
    onSelectSurface?.("ALL")
    onSelectCondition?.("OBSERVACION")
  }

  const switchToSurfaceMode = () => {
    onSelectSurface?.(
      normalizedSelectedSurface && normalizedSelectedSurface !== "ALL"
        ? normalizedSelectedSurface
        : "O"
    )
    onSelectCondition?.("CARIES")
  }

  const handlePickSurface = (surface) => {
    onSelectSurface?.(surface)
  }

  const handlePickCondition = (condition) => {
    onSelectCondition?.(condition)
  }

  const currentTargetText = selectedTooth
    ? selectedMode === "TOOTH"
      ? `Pieza ${selectedTooth} · Diente completo`
      : `Pieza ${selectedTooth} · ${getSurfaceLabel(normalizedSelectedSurface)}`
    : "Sin selección"

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div
        style={{
          borderRadius: 18,
          border: "1px solid #e2e8f0",
          background: "#ffffff",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
          padding: 14,
          display: "grid",
          gap: 14,
        }}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "#64748b",
                marginBottom: 6,
              }}
            >
              Odontograma clínico
            </div>

            <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.5 }}>
              Vista general de hallazgos por pieza. Toca una pieza para abrir el editor contextual y trabajar por separado <strong>diente completo</strong> y <strong>superficie</strong>.
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap"
            }}
          >
            <QuickInfoPill
              label="Piezas con hallazgos"
              value={String(odontogramSummary.teethWithFindings)}
            />
            <QuickInfoPill
              label="Condiciones generales"
              value={String(odontogramSummary.generalConditions)}
            />
            <QuickInfoPill
              label="Superficies con hallazgos"
              value={String(odontogramSummary.surfacesWithFindings)}
            />
            <QuickInfoPill
              label="Entradas totales"
              value={String(odontogramSummary.totalEntries)}
            />
          </div>

          <LegendRow />

          <OdontogramRow
            title="Arcada superior"
            teeth={TOP_TEETH}
            toothState={toothState}
            selectedTooth={selectedTooth}
            onSelectTooth={onSelectTooth}
          />

          <OdontogramRow
            title="Arcada inferior"
            teeth={BOTTOM_TEETH}
            toothState={toothState}
            selectedTooth={selectedTooth}
            onSelectTooth={onSelectTooth}
          />
        </div>
      </div>

      {selectedTooth ? (
        <div style={overlayStyle} onMouseDown={closeEditor}>
          <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={modalEyebrowStyle}>Editor de pieza</div>
                <div style={modalTitleStyle}>Pieza {selectedTooth}</div>
                <div style={modalSubtitleStyle}>{currentTargetText}</div>
              </div>

              <button type="button" onClick={closeEditor} style={closeButtonStyle}>
                Cerrar
              </button>
            </div>

            <div style={modalBodyStyle}>
              <div style={leftColumnStyle}>
                <div style={panelCardStyle}>
                  <div style={sectionMiniTitleStyle}>Vista rápida</div>
                  <LargeToothPreview tooth={selectedTooth} data={currentToothData} />
                </div>

                <div style={panelCardStyle}>
                  <div style={sectionMiniTitleStyle}>Qué estás editando</div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                    <SegmentButton
                      active={selectedMode === "TOOTH"}
                      onClick={switchToToothMode}
                    >
                      Diente completo
                    </SegmentButton>

                    <SegmentButton
                      active={selectedMode === "SURFACE"}
                      onClick={switchToSurfaceMode}
                    >
                      Superficie
                    </SegmentButton>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <SelectionInfoCard
                      label="Aplicará a"
                      value={
                        selectedMode === "TOOTH"
                          ? `Todo el diente ${selectedTooth}`
                          : `${getSurfaceLabel(normalizedSelectedSurface)} de la pieza ${selectedTooth}`
                      }
                      tone={selectedMode === "TOOTH" ? "neutral" : "info"}
                    />
                  </div>

                  {selectedMode === "SURFACE" ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={fieldLabelStyle}>Selecciona una superficie</div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {SURFACE_OPTIONS.map((item) => (
                          <SegmentButton
                            key={item.value}
                            active={normalizedSelectedSurface === item.value}
                            onClick={() => handlePickSurface(item.value)}
                          >
                            {item.label}
                          </SegmentButton>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div style={panelCardStyle}>
                  <div style={sectionMiniTitleStyle}>Acción</div>

                  <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                    <SelectionInfoCard
                      label="Condición seleccionada"
                      value={
                        getConditionMeta(normalizeCondition(selectedCondition)).label ||
                        "Sin condición"
                      }
                      tone="success"
                    />

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={onSave}
                        disabled={saving || !selectedTooth || !selectedCondition}
                        style={{
                          ...primaryActionButtonStyle,
                          opacity: saving || !selectedTooth || !selectedCondition ? 0.7 : 1,
                          cursor:
                            saving || !selectedTooth || !selectedCondition
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {saving ? "Guardando..." : "Guardar hallazgo"}
                      </button>

                      <button
                        type="button"
                        onClick={onSendToPlan}
                        disabled={!selectedTooth || !selectedCondition}
                        style={{
                          ...secondaryActionButtonStyle,
                          opacity: !selectedTooth || !selectedCondition ? 0.7 : 1,
                          cursor:
                            !selectedTooth || !selectedCondition
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        Enviar a plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div style={rightColumnStyle}>
                <div style={panelCardStyle}>
                  <div style={sectionMiniTitleStyle}>Elige la condición</div>

                  <div style={conditionPickerGridStyle}>
                    {conditionOptions.map((item) => {
                      const visual = getConditionMeta(item.value)
                      const active =
                        normalizeCondition(selectedCondition) === item.value

                      return (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => handlePickCondition(item.value)}
                          style={{
                            borderRadius: 14,
                            padding: "10px 12px",
                            border: active
                              ? `2px solid ${visual.color}`
                              : `1px solid ${visual.border}`,
                            background: active ? visual.bg : "#ffffff",
                            color: visual.color,
                            fontWeight: 800,
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 900 }}>
                            {item.label}
                          </div>
                          <div style={{ fontSize: 11, opacity: 0.78, marginTop: 2 }}>
                            {selectedMode === "TOOTH"
                              ? "Condición general"
                              : "Condición localizada"}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div style={panelCardStyle}>
                  <div style={sectionMiniTitleStyle}>Condiciones actuales de la pieza</div>

                  <div style={conditionsSummaryLayoutStyle}>
                    <div>
                      <div style={groupTitleStyle}>
                        Generales ({currentToothData?.general?.length || 0})
                      </div>
                      <ConditionList
                        items={currentToothData?.general || []}
                        emptyLabel="Sin condiciones generales registradas."
                        formatDateTime={formatDateTime}
                      />
                    </div>

                    <div>
                      <div style={groupTitleStyle}>Por superficie</div>
                      <SurfaceConditionList
                        surfaces={currentToothData?.surfaces}
                        formatDateTime={formatDateTime}
                      />
                    </div>
                  </div>
                </div>

                <div style={panelCardStyle}>
                  <div style={sectionMiniTitleStyle}>Lectura rápida</div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                    <QuickInfoPill
                      label="Generales"
                      value={String(currentToothData?.general?.length || 0)}
                    />
                    <QuickInfoPill
                      label="Superficies con hallazgos"
                      value={String(
                        Object.values(currentToothData?.surfaces || {}).filter(
                          (items) => Array.isArray(items) && items.length > 0
                        ).length
                      )}
                    />
                    <QuickInfoPill
                      label="Total de condiciones"
                      value={String(
                        (currentToothData?.general?.length || 0) +
                          Object.values(currentToothData?.surfaces || {}).flat().length
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function LegendRow() {
  const items = [
    { label: "Caries", meta: getConditionMeta("CARIES") },
    { label: "Restauración", meta: getConditionMeta("RESTAURACION") },
    { label: "Endodoncia / Implante", meta: getConditionMeta("ENDODONCIA") },
    { label: "Corona / Movilidad", meta: getConditionMeta("CORONA") },
    { label: "Sano / Desgaste", meta: getConditionMeta("SANO") },
    { label: "Ausente / Extraído", meta: getConditionMeta("AUSENTE") },
  ]

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {items.map((item) => (
        <div
          key={item.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 9px",
            borderRadius: 999,
            border: `1px solid ${item.meta.border}`,
            background: "#f8fafc",
            fontSize: 11,
            color: item.meta.color,
            fontWeight: 800,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              minWidth: 10,
              borderRadius: "50%",
              background: item.meta.color,
              boxShadow: `0 0 0 3px ${item.meta.bg}`,
              border: `1px solid ${item.meta.border}`,
            }}
          />
          {item.label}
        </div>
      ))}
    </div>
  )
}

function OdontogramRow({ title, teeth, toothState, selectedTooth, onSelectTooth }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 900,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.5
        }}
      >
        {title}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(8, minmax(72px, 1fr))",
          gap: 6,
        }}
      >
        {teeth.map((tooth) => (
          <ToothCard
            key={tooth}
            tooth={tooth}
            data={toothState[tooth]}
            selected={Number(selectedTooth) === Number(tooth)}
            onClick={() => onSelectTooth?.(tooth)}
          />
        ))}
      </div>
    </div>
  )
}

function ToothCard({ tooth, data, selected, onClick }) {
  const general = data?.general || []
  const surfaces = data?.surfaces || {}
  const totalSurfaceConditions = Object.values(surfaces).flat().length
  const totalConditions = general.length + totalSurfaceConditions
  const dominantGeneralCondition = getDominantGeneralCondition(general)
  const dominantGeneralVisual = dominantGeneralCondition
    ? getConditionMeta(dominantGeneralCondition)
    : null

  const generalShorts = general.slice(0, 2)
  const hasFindings = totalConditions > 0

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 14,
        border: selected
          ? "2px solid #2563eb"
          : hasFindings
            ? "1px solid #dbeafe"
            : "1px solid #e2e8f0",
        background: selected
          ? "#f8fbff"
          : hasFindings
            ? "#ffffff"
            : "#fbfdff",
        padding: 7,
        cursor: "pointer",
        boxShadow: selected
          ? "0 10px 20px rgba(37, 99, 235, 0.12)"
          : hasFindings
            ? "0 4px 12px rgba(15,23,42,0.04)"
            : "none",
        textAlign: "left"
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto",
          alignItems: "center",
          gap: 4,
          marginBottom: 6
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 900,
            color: hasFindings ? "#0f172a" : "#64748b"
          }}
        >
          {tooth}
        </div>

        {general.length > 0 ? (
          <MiniTopBadge tone="warning">G{general.length}</MiniTopBadge>
        ) : null}

        {totalSurfaceConditions > 0 ? (
          <MiniTopBadge tone="info">S{totalSurfaceConditions}</MiniTopBadge>
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateAreas: `
            ". O ."
            "M C D"
            ". V L"
          `,
          gridTemplateColumns: "1fr 1fr 1fr",
          gridTemplateRows: "1fr 1fr 1fr",
          gap: 4,
          marginBottom: 6
        }}
      >
        <SurfaceCell area="O" entries={surfaces.O || []} />
        <SurfaceCell area="M" entries={surfaces.M || []} />
        <SurfaceCell area="D" entries={surfaces.D || []} />
        <SurfaceCell area="V" entries={surfaces.V || []} />
        <SurfaceCell area="L" entries={surfaces.L || []} />

        <div
          style={{
            gridArea: "C",
            borderRadius: 9,
            border: dominantGeneralVisual
              ? `1px solid ${dominantGeneralVisual.border}`
              : "1px solid #d7dee9",
            background: dominantGeneralVisual
              ? dominantGeneralVisual.bg
              : hasFindings
                ? "#ffffff"
                : "#f8fafc",
            minHeight: 28,
            display: "grid",
            placeItems: "center",
            fontSize: 11,
            fontWeight: 900,
            color: dominantGeneralVisual
              ? dominantGeneralVisual.color
              : hasFindings
                ? "#334155"
                : "#94a3b8",
            position: "relative"
          }}
        >
          {general.length > 0 ? `G${general.length}` : tooth}

          {totalConditions > 0 ? (
            <div
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                minWidth: 16,
                height: 16,
                borderRadius: 999,
                background: "#0f172a",
                color: "#ffffff",
                fontSize: 9,
                fontWeight: 900,
                display: "grid",
                placeItems: "center",
                padding: "0 3px",
              }}
            >
              {totalConditions}
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          minHeight: 20,
          display: "flex",
          gap: 4,
          flexWrap: "wrap"
        }}
      >
        {generalShorts.map((item) => {
          const visual = getConditionMeta(item.condition)
          return (
            <div
              key={item.id}
              style={{
                fontSize: 9,
                fontWeight: 900,
                padding: "2px 5px",
                borderRadius: 999,
                background: visual.bg,
                border: `1px solid ${visual.border}`,
                color: visual.color
              }}
            >
              {visual.short}
            </div>
          )
        })}

        {general.length > 2 ? (
          <div
            style={{
              fontSize: 9,
              fontWeight: 900,
              padding: "2px 5px",
              borderRadius: 999,
              background: "#f8fafc",
              border: "1px solid #cbd5e1",
              color: "#334155"
            }}
          >
            +{general.length - 2}
          </div>
        ) : null}
      </div>
    </button>
  )
}

function SurfaceCell({ area, entries }) {
  const first = entries?.[0]
  const visual = first ? getConditionMeta(first.condition) : null

  return (
    <div
      style={{
        gridArea: area,
        minHeight: 14,
        borderRadius: 6,
        border: visual ? `1px solid ${visual.border}` : "1px solid #e5e7eb",
        background: visual ? visual.bg : "#fbfdff",
        position: "relative",
      }}
      title={first ? visual.label : ""}
    >
      {entries?.length ? (
        <div
          style={{
            position: "absolute",
            top: -5,
            right: -5,
            minWidth: 13,
            height: 13,
            borderRadius: 999,
            background: visual.color,
            color: "#ffffff",
            fontSize: 8,
            fontWeight: 900,
            display: "grid",
            placeItems: "center",
            padding: "0 3px",
          }}
        >
          {entries.length}
        </div>
      ) : null}
    </div>
  )
}

function LargeToothPreview({ tooth, data }) {
  const general = data?.general || []
  const surfaces = data?.surfaces || {}
  const dominantGeneralCondition = getDominantGeneralCondition(general)
  const dominantGeneralVisual = dominantGeneralCondition
    ? getConditionMeta(dominantGeneralCondition)
    : null

  return (
    <div
      style={{
        borderRadius: 18,
        border: "1px solid #dbe3ef",
        background: "#f8fafc",
        padding: 12,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateAreas: `
            ". O ."
            "M C D"
            ". V L"
          `,
          gridTemplateColumns: "1fr 1.25fr 1fr",
          gridTemplateRows: "1fr 1.25fr 1fr",
          gap: 8,
          minHeight: 144,
        }}
      >
        <LargeSurfaceCell area="O" entries={surfaces.O || []} label="O" />
        <LargeSurfaceCell area="M" entries={surfaces.M || []} label="M" />
        <LargeSurfaceCell area="D" entries={surfaces.D || []} label="D" />
        <LargeSurfaceCell area="V" entries={surfaces.V || []} label="V" />
        <LargeSurfaceCell area="L" entries={surfaces.L || []} label="L" />

        <div
          style={{
            gridArea: "C",
            borderRadius: 18,
            border: dominantGeneralVisual
              ? `1px solid ${dominantGeneralVisual.border}`
              : "1px solid #cbd5e1",
            background: dominantGeneralVisual ? dominantGeneralVisual.bg : "#ffffff",
            display: "grid",
            placeItems: "center",
            padding: 8,
          }}
        >
          <div
            style={{
              fontSize: 28,
              fontWeight: 900,
              color: dominantGeneralVisual ? dominantGeneralVisual.color : "#0f172a",
              lineHeight: 1,
            }}
          >
            {general.length > 0 ? `G${general.length}` : tooth}
          </div>

          <div
            style={{
              marginTop: 6,
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {general.slice(0, 4).map((item) => {
              const visual = getConditionMeta(item.condition)
              return (
                <div
                  key={item.id}
                  style={{
                    fontSize: 10,
                    fontWeight: 900,
                    padding: "3px 6px",
                    borderRadius: 999,
                    background: visual.bg,
                    border: `1px solid ${visual.border}`,
                    color: visual.color,
                  }}
                >
                  {visual.short}
                </div>
              )
            })}

            {general.length > 4 ? (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 900,
                  padding: "3px 6px",
                  borderRadius: 999,
                  background: "#f8fafc",
                  border: "1px solid #cbd5e1",
                  color: "#334155",
                }}
              >
                +{general.length - 4}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

function LargeSurfaceCell({ area, entries, label }) {
  const first = entries?.[0]
  const visual = first ? getConditionMeta(first.condition) : null

  return (
    <div
      style={{
        gridArea: area,
        borderRadius: 14,
        border: visual ? `1px solid ${visual.border}` : "1px solid #e2e8f0",
        background: visual ? visual.bg : "#ffffff",
        minHeight: 28,
        padding: 6,
        display: "grid",
        placeItems: "center",
        position: "relative",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          color: visual ? visual.color : "#94a3b8",
        }}
      >
        {label}
      </div>

      {entries?.length ? (
        <div
          style={{
            position: "absolute",
            top: -6,
            right: -6,
            minWidth: 18,
            height: 18,
            borderRadius: 999,
            background: visual.color,
            color: "#ffffff",
            fontSize: 10,
            fontWeight: 900,
            display: "grid",
            placeItems: "center",
            padding: "0 4px",
          }}
        >
          {entries.length}
        </div>
      ) : null}
    </div>
  )
}

function SegmentButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: "8px 12px",
        border: active ? "1px solid #2563eb" : "1px solid #dbe3ef",
        background: active ? "#eff6ff" : "#ffffff",
        color: active ? "#1d4ed8" : "#334155",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  )
}

function SelectionInfoCard({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: "#f8fafc",
      border: "#e2e8f0",
      color: "#334155",
    },
    info: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    },
    success: {
      bg: "#f0fdf4",
      border: "#bbf7d0",
      color: "#166534",
    },
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        borderRadius: 14,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        padding: 12,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "#64748b",
          marginBottom: 4,
        }}
      >
        {label}
      </div>

      <div style={{ fontSize: 14, fontWeight: 900, color: visual.color }}>
        {value}
      </div>
    </div>
  )
}

function ConditionList({ items, emptyLabel, formatDateTime }) {
  if (!items?.length) {
    return <div style={emptyStateTextStyle}>{emptyLabel}</div>
  }

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {items.map((item) => (
        <ConditionRow
          key={item.id}
          item={item}
          formatDateTime={formatDateTime}
        />
      ))}
    </div>
  )
}

function SurfaceConditionList({ surfaces, formatDateTime }) {
  const list = SURFACE_OPTIONS.map((surface) => ({
    ...surface,
    entries: surfaces?.[surface.value] || [],
  }))

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {list.map((surface) => (
        <div
          key={surface.value}
          style={{
            borderRadius: 14,
            border: "1px solid #e2e8f0",
            background: "#f8fafc",
            padding: 10,
          }}
        >
          <div style={surfaceTitleStyle}>
            {surface.label} ({surface.entries.length})
          </div>

          {!surface.entries.length ? (
            <div style={emptyStateTextStyle}>Sin hallazgos en esta superficie.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {surface.entries.map((item) => (
                <ConditionRow
                  key={item.id}
                  item={item}
                  formatDateTime={formatDateTime}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function ConditionRow({ item, formatDateTime }) {
  const visual = getConditionMeta(item.condition)

  return (
    <div
      style={{
        borderRadius: 12,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        padding: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: visual.color,
          }}
        >
          {visual.label}
        </div>

        <StatusPill>{getStatusLabel(item.status)}</StatusPill>

        {item.definitionType === "SURFACE" && item.surface ? (
          <StatusPill>{getSurfaceLabel(item.surface)}</StatusPill>
        ) : (
          <StatusPill>Diente completo</StatusPill>
        )}
      </div>

      {item.notes ? (
        <div style={{ marginTop: 6, color: "#334155", fontSize: 13 }}>
          {item.notes}
        </div>
      ) : null}

      <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>
        {formatDateSafe(item.createdAt, formatDateTime)}
      </div>
    </div>
  )
}

function StatusPill({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#475569",
        padding: "3px 6px",
        borderRadius: 999,
        background: "#ffffff",
        border: "1px solid rgba(148, 163, 184, 0.35)",
      }}
    >
      {children}
    </div>
  )
}

function QuickInfoPill({ label, value }) {
  return (
    <div
      style={{
        padding: "8px 10px",
        borderRadius: 12,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: "#0f172a", fontWeight: 900, marginTop: 2 }}>
        {value}
      </div>
    </div>
  )
}

function MiniTopBadge({ children, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: "#f8fafc",
      border: "#cbd5e1",
      color: "#334155",
    },
    info: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8",
    },
    warning: {
      bg: "#fffbeb",
      border: "#fde68a",
      color: "#a16207",
    },
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 900,
        padding: "3px 6px",
        borderRadius: 999,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
      }}
    >
      {children}
    </div>
  )
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 999999,
  background: "rgba(15, 23, 42, 0.32)",
  backdropFilter: "blur(3px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 20,
}

const modalStyle = {
  width: "min(1180px, 96vw)",
  maxHeight: "92vh",
  overflow: "hidden",
  borderRadius: 24,
  background: "#f8fafc",
  boxShadow: "0 30px 80px rgba(15, 23, 42, 0.35)",
  display: "flex",
  flexDirection: "column",
}

const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  padding: "18px 20px 14px",
  borderBottom: "1px solid #e2e8f0",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
}

const modalEyebrowStyle = {
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "#64748b",
  marginBottom: 6,
}

const modalTitleStyle = {
  fontSize: 30,
  fontWeight: 900,
  lineHeight: 1.02,
  letterSpacing: "-0.03em",
  color: "#0f172a",
}

const modalSubtitleStyle = {
  marginTop: 6,
  fontSize: 14,
  color: "#475569",
}

const closeButtonStyle = {
  borderRadius: 12,
  padding: "10px 14px",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#334155",
  fontWeight: 800,
  cursor: "pointer",
}

const modalBodyStyle = {
  padding: 20,
  overflow: "auto",
  display: "grid",
  gridTemplateColumns: "360px minmax(0, 1fr)",
  gap: 16,
}

const leftColumnStyle = {
  display: "grid",
  gap: 16,
  alignContent: "start",
}

const rightColumnStyle = {
  display: "grid",
  gap: 16,
  alignContent: "start",
}

const panelCardStyle = {
  borderRadius: 18,
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
  padding: 16,
}

const sectionMiniTitleStyle = {
  fontSize: 12,
  color: "#64748b",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 0.5,
}

const fieldLabelStyle = {
  fontSize: 12,
  color: "#475569",
  fontWeight: 800,
  marginBottom: 8,
}

const conditionPickerGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  marginTop: 10,
}

const conditionsSummaryLayoutStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
  gap: 14,
  marginTop: 10,
}

const groupTitleStyle = {
  fontSize: 12,
  fontWeight: 900,
  color: "#334155",
  marginBottom: 8,
}

const surfaceTitleStyle = {
  fontSize: 12,
  fontWeight: 900,
  color: "#334155",
  marginBottom: 8,
}

const emptyStateTextStyle = {
  color: "#64748b",
  fontSize: 13,
}

const primaryActionButtonStyle = {
  borderRadius: 14,
  padding: "11px 16px",
  border: "none",
  background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
  color: "#ffffff",
  fontWeight: 900,
}

const secondaryActionButtonStyle = {
  borderRadius: 14,
  padding: "11px 16px",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#334155",
  fontWeight: 900,
}